// src/components/Auth/AuthCallback.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // First check if we already have a session (Supabase may have auto-established it)
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        
        if (existingSession) {
          console.log('Session already exists, redirecting to dashboard...');
          navigate('/home', { replace: true });
          return;
        }

        // Get the hash fragment from the URL
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const errorCode = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');

        if (errorCode) {
          console.error('OAuth error:', errorCode, errorDescription);
          setError(errorDescription || errorCode);
          
          // Redirect to login with error after a delay
          setTimeout(() => {
            navigate('/login', { 
              state: { error: errorDescription || 'Authentication failed' } 
            });
          }, 3000);
          return;
        }

        // Only try to exchange code if we have the proper URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        
        if (code) {
          // We have a code, try to exchange it
          const { data, error } = await supabase.auth.exchangeCodeForSession(
            window.location.href
          );

          if (error) {
            console.error('Session exchange error:', error);
            setError(error.message);
            
            // Redirect to login with error
            setTimeout(() => {
              navigate('/login', { 
                state: { error: error.message } 
              });
            }, 3000);
            return;
          }

          if (data?.session) {
            console.log('Authentication successful, redirecting to dashboard...');
            navigate('/home', { replace: true });
          }
        } else {
          // No code and no session, probably a direct navigation
          console.log('No auth code found, redirecting to login...');
          navigate('/login', { replace: true });
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('An unexpected error occurred');
        
        // Redirect to login on any error
        setTimeout(() => {
          navigate('/login', { 
            state: { error: 'Authentication failed. Please try again.' } 
          });
        }, 3000);
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="text-center">
        {error ? (
          <div className="space-y-4">
            <div className="text-red-400 text-xl font-semibold">
              Authentication Failed
            </div>
            <div className="text-red-300">
              {error}
            </div>
            <div className="text-purple-300 text-sm">
              Redirecting to login...
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
            <div className="text-purple-300">
              Completing authentication...
            </div>
          </div>
        )}
      </div>
    </div>
  );
}